import type { MealComponent } from '@/types';

interface DishMacros {
  protein_g: number;
  fat_g: number;
  carbs_g: number;
}

/**
 * Раскладывает Б/Ж/У всего блюда по его компонентам пропорционально их калориям.
 *
 * Зачем: analyze-meal в быстром режиме отдаёт компоненты составного блюда только с
 * name/weight_g/calories (без макросов). А ComponentsEditor при любом изменении состава
 * пересчитывает итог блюда, СУММИРУЯ Б/Ж/У компонентов. Если у компонентов макросов нет —
 * сумма = 0, и правка состава обнуляет Б/Ж/У блюда (калории при этом выживают).
 * Раскладка макросов по компонентам заранее чинит это: сумма компонентов = исходное блюдо.
 *
 * Идемпотентна и безопасна:
 *   - нет компонентов → [];
 *   - у компонентов УЖЕ есть макросы → возвращаем как есть (не перетираем);
 *   - у блюда нет макросов для раскладки → возвращаем как есть.
 */
export function backfillComponentMacros(
  components: MealComponent[] | undefined | null,
  dish: DishMacros,
): MealComponent[] {
  if (!Array.isArray(components) || components.length === 0) return [];

  const alreadyHasMacros = components.some(
    (c) => Number(c.protein_g) > 0 || Number(c.fat_g) > 0 || Number(c.carbs_g) > 0,
  );
  if (alreadyHasMacros) return components;

  const dishP = Number(dish.protein_g) || 0;
  const dishF = Number(dish.fat_g) || 0;
  const dishC = Number(dish.carbs_g) || 0;
  if (dishP <= 0 && dishF <= 0 && dishC <= 0) return components;

  const totalCal = components.reduce((s, c) => s + (Number(c.calories) || 0), 0);
  const n = components.length;

  return components.map((c) => {
    const share = totalCal > 0 ? (Number(c.calories) || 0) / totalCal : 1 / n;
    return {
      ...c,
      protein_g: +(dishP * share).toFixed(1),
      fat_g: +(dishF * share).toFixed(1),
      carbs_g: +(dishC * share).toFixed(1),
    };
  });
}
